import { useId, type ReactNode } from 'react';
import { ChevronDown, History } from 'lucide-react';
import { fuelText, type Fuel } from './fuel';
import { formatLead } from './velocity';
import type { WorkspaceFuel } from './useWorkspaceFuel';
import { useStoredFlag } from './useStoredFlag';

export const HUD_STORAGE_KEY = 'aiai.mc.hud';

const DOT = <span aria-hidden="true" className="text-ink-faint">{'\u00b7'}</span>;

/** One fact on the slim line: the value, then a small label (dt before dd for screen readers). */
function Stat({
  value, label, shown, sep = false,
}: { value: string | number; label: string; shown?: ReactNode; sep?: boolean }) {
  return (
    <div className="flex items-baseline gap-1">
      <dt className="order-2 font-display text-2xs font-bold uppercase tracking-[0.08em] text-ink-muted">
        {shown === undefined ? <span className="sr-only">{label}</span> : (
          <>
            <span className="sr-only">{label}</span>
            <span aria-hidden="true">{shown}</span>
          </>
        )}
      </dt>
      <dd className="order-1 text-sm font-semibold tabular-nums text-ink">
        {sep && <span aria-hidden="true" className="mr-1 font-normal text-ink-faint">{'\u00b7'}</span>}
        {value}
      </dd>
    </div>
  );
}

const LEVEL_RANK = { ok: 0, low: 1, grounded: 2 } as const;

/** The emptiest tank across the workspaces (grounded, then low, then the fullest ratio), if any. */
function worstTank(fuel: WorkspaceFuel[]): Extract<Fuel, { kind: 'tank' }> | null {
  let worst: Extract<Fuel, { kind: 'tank' }> | null = null;
  for (const { fuel: f } of fuel) {
    if (f.kind !== 'tank') continue;
    if (!worst || LEVEL_RANK[f.level] > LEVEL_RANK[worst.level]
      || (LEVEL_RANK[f.level] === LEVEL_RANK[worst.level] && f.ratio > worst.ratio)) worst = f;
  }
  return worst;
}

/**
 * The scene's Mission control panel, one slim line by default:
 * `6 shipped 24h \u00b7 $4.37 \u00b7 [fuel] \u00b7 TV [playback] v`. The toggle expands the full card
 * (fuel per workspace, open issues, fastest launch) below it; the choice is remembered.
 * Purely informational (the live count and stop-all live in the Live drawer), so it floats over the scene
 * without blocking planet clicks.
 */
export function MissionControlHud({
  loaded, shipped24h, spend24hUsd, openIssueCount, fuel, fastest, onTv, onPlayback,
}: {
  loaded: boolean; shipped24h: number; spend24hUsd: number; openIssueCount: number;
  fuel: WorkspaceFuel[]; fastest: { code: string; minutes: number } | null;
  /** Enters TV mode. */
  onTv: () => void;
  /** Opens Playback (replay a recent period). */
  onPlayback: () => void;
}) {
  const [expanded, setExpanded] = useStoredFlag(HUD_STORAGE_KEY, false);
  const detailsId = `mc-details${useId().replace(/:/g, '')}`;
  const show = (v: string | number) => (loaded ? v : '—');
  const tank = worstTank(fuel);
  const fuelSummary = fuel.map(({ workspace, fuel: f }) => fuelText(workspace.name, f)).join('; ');
  const issuesLine = openIssueCount > 0
    ? `${openIssueCount} open issue${openIssueCount === 1 ? '' : 's'} across your workspaces`
    : 'No open issues';
  const fastestLine = fastest ? `Fastest launch this week: ${fastest.code} in ${formatLead(fastest.minutes)}` : null;
  // The card's longer lines ride on the title while collapsed (the fuel mini bar carries its own).
  const title = [loaded ? issuesLine : '', loaded ? fastestLine : ''].filter(Boolean).join('\n');
  const tankHot = tank && (tank.level === 'low' || tank.level === 'grounded');

  return (
    <div
      role="group"
      aria-label="Mission control"
      title={title || undefined}
      // pointer-events-auto: the wrapper lets clicks through to the scene, but the tooltips need hover.
      className="pointer-events-auto inline-block max-w-full bg-surface-1 sm:rounded-card sm:border sm:border-line sm:bg-surface-1/90 sm:backdrop-blur-sm"
    >
      <div data-testid="hud-bar" className="flex h-8 items-center gap-2 whitespace-nowrap px-2.5">
        <dl className="flex items-baseline gap-x-1">
          <Stat
            value={show(shipped24h)} label="shipped 24h"
            shown={<>shipped<span className="hidden sm:inline"> 24h</span></>}
          />
          <Stat value={show(`$${spend24hUsd.toFixed(2)}`)} label="spend 24h" sep />
        </dl>
        {tank && (
          <>
            {DOT}
            <span
              role="img"
              aria-label={`Fuel: ${fuelSummary}`}
              title={fuelSummary}
              data-testid="fuel-mini"
              data-level={tank.level}
              className="pointer-events-auto h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-surface-3"
            >
              <span
                className={`block h-full ${tankHot ? 'bg-signal' : 'bg-ink-muted'}`}
                style={{ width: `${Math.round(tank.ratio * 1000) / 10}%` }}
              />
            </span>
          </>
        )}
        {DOT}
        <button
          type="button"
          aria-label="TV mode"
          onClick={onTv}
          className="pointer-events-auto rounded-card border border-line px-1.5 font-display text-2xs font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-ink"
        >
          TV
        </button>
        <button
          type="button"
          aria-label="Playback"
          title="Playback"
          onClick={onPlayback}
          className="pointer-events-auto rounded-[5px] p-0.5 text-ink-muted hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        >
          <History className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={detailsId}
          aria-label={expanded ? 'Hide mission details' : 'Show mission details'}
          title={expanded ? 'Less' : 'More'}
          onClick={() => setExpanded(!expanded)}
          className="pointer-events-auto -mr-1 rounded-[5px] p-0.5 text-ink-muted hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </div>
      {expanded && (
        <div id={detailsId} className="border-t border-line px-3 pb-2 pt-1.5">
          {fuel.length > 0 && (
            <div role="group" aria-label="Fuel">
              <div className="font-display text-2xs font-bold uppercase tracking-[0.08em] text-ink-muted">Fuel</div>
              {fuel.map(({ workspace, fuel: f }) => {
                const t = f.kind === 'tank' ? f : null;
                const level = t ? t.level : 'unlimited';
                const hot = level === 'low' || level === 'grounded';
                return (
                  <div key={workspace.id} data-testid="fuel-tank" data-level={level} className="flex items-center gap-2 text-2xs">
                    {t && (
                      <span aria-hidden="true" className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-3">
                        <span
                          className={`block h-full ${hot ? 'bg-signal' : 'bg-ink-muted'}`}
                          style={{ width: `${Math.round(t.ratio * 1000) / 10}%` }}
                        />
                      </span>
                    )}
                    {level === 'low' && (
                      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-signal animate-pulse-soft motion-reduce:animate-none" style={{ animationDuration: '2.4s' }} />
                    )}
                    {level === 'grounded' && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-signal" />}
                    <span className={level === 'grounded' ? 'font-semibold text-signal' : 'text-ink-muted'}>
                      {fuelText(workspace.name, f)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {loaded && <p className="mt-1 text-2xs text-ink-muted">{issuesLine}</p>}
          {loaded && fastestLine && <p className="mt-0.5 text-2xs text-ink-muted">{fastestLine}</p>}
        </div>
      )}
    </div>
  );
}
